import { get } from 'svelte/store'
import { createMutation, createQuery } from '@tanstack/svelte-query'
import { storeScene } from '../../../stores/scene'
import queryClient from '../../../constants/query-client'
import { focusOnObject } from '../../../stores/camera'
import { type Object3D } from 'three'
import { ThreeItemKeys, ThreeItemTypes } from '../../../constants/three'
import { canvasSelection, pruneMissingFromMultiSelection } from '../../../stores/canvas-selection'
import { restoreOriginalMaterial } from '../../../helpers/selection-material'
import { updateDxfLayers as updateDxfLayersHelper } from '../../../helpers/dxf-layers'

const useObjectQueries = () => {
  const getObject = (objectUuid?: Object3D['uuid']) => {
    const foundObject = get(storeScene)?.getObjectByProperty('uuid', objectUuid)

    if (!foundObject) {
      throw new Error('object not found')
    }

    return foundObject
  }

  const invalidateCanvas = () => {
    queryClient.invalidateQueries({ queryKey: ['canvas'] })
  }

  const useObject = (objectUuid?: Object3D['uuid']) => createQuery(() => ({
    queryKey: ['canvas', 'objects', objectUuid],
    queryFn: () => getObject(objectUuid),
    enabled: !!objectUuid,
  }), () => queryClient)

  const updateDxfLayers = (objectUuid: Object3D['uuid']) => {
    const object = getObject(objectUuid)
    if (!object) return

    updateDxfLayersHelper(object)
  }

  const updateUserDataMutation = createMutation(() => ({
    mutationFn: ({ objectUuid, object }: { objectUuid: Object3D['uuid'], object: Record<string, string | boolean | number | undefined | null> }) => new Promise((resolve) => {
      const originalObject = getObject(objectUuid)

      originalObject.userData = {
        ...originalObject.userData,
        ...object,
      }

      resolve(originalObject)
    }),
    onSuccess: (_data, { objectUuid }) => {
      invalidateCanvas()
      updateDxfLayers(objectUuid)
    },
  }), () => queryClient)

  const changeVisibility = (visibility: boolean) => createMutation(() => ({
    mutationFn: (objectUuid: Object3D['uuid']) => new Promise((resolve) => {
      const object = getObject(objectUuid)
      object.visible = visibility
      resolve(object)
    }),
    onSuccess: (_data, objectUuid) => {
      const object = getObject(objectUuid)

      if (object.userData[ThreeItemKeys.ITEM_TYPE] === ThreeItemTypes.PRINTER) {
        get(storeScene)?.traverse((child) => {
          if (child.userData[ThreeItemKeys.ASSIGNED_PRINTER_ID] === object.userData[ThreeItemKeys.PRINTER_ID]) {
            child.visible = visibility
          }
        })
      }

      invalidateCanvas()
    },
  }), () => queryClient)

  const hideMutation = changeVisibility(false)
  const showMutation = changeVisibility(true)

  const removeMutation = createMutation(() => ({
    mutationFn: (objectUuid: Object3D['uuid']) => new Promise((resolve) => {
      const object = getObject(objectUuid)
      const selectedElement = get(canvasSelection)

      if (selectedElement?.uuid === objectUuid) {
        restoreOriginalMaterial(selectedElement)
        canvasSelection.set(undefined)
      }
      object?.parent?.remove(object)

      resolve(true)
    }),
    onSuccess: () => {
      pruneMissingFromMultiSelection()
      invalidateCanvas()
    },
  }), () => queryClient)

  const renameMutation = createMutation(() => ({
    mutationFn: ({ objectUuid, name }: { objectUuid: Object3D['uuid'], name: string }) => new Promise((resolve) => {
      const object = getObject(objectUuid)
      object.name = name
      resolve(object)
    }),
    onSuccess: invalidateCanvas,
  }), () => queryClient)

  const focusCamera = (objectUuid: Object3D['uuid']) => {
    const group = getObject(objectUuid)
    if (group) focusOnObject(group)
  }

  return {
    hideMutation,
    showMutation,
    removeMutation,
    renameMutation,
    focusCamera,
    useObject,
    updateUserDataMutation,
    invalidateCanvas,
    getObject,
  }
}

export default useObjectQueries
